import { useEffect, useRef } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useProfileContext } from "context";
import * as api from "api/bAIAssistantKnowledgeApi";

export const POLL_INTERVAL = 3000;
export const POLL_WINDOW = 120000;
export const isSourceBusy = (source) => !!source && (source.reserved || source.sync_status === "running"
  || ["queued", "running", "retry_wait", "reserved"].includes(source.activity_status));

// One bounded observation window per mounted query/tenant, including a page
// opened while a scheduled run is already active. No background-tab polling.
export function knowledgePollInterval(query, state, now = Date.now()) {
  const data = query.state.data;
  const busy = data?.items ? data.items.some(isSourceBusy) : isSourceBusy(data);
  if (!busy) { state.started = null; state.paused = false; return false; }
  if (query.state.status === "error") return false;
  if (state.started === null) state.started = now;
  if (now - state.started >= POLL_WINDOW) { state.paused = true; return false; }
  return POLL_INTERVAL;
}

function useKnowledgeScope() {
  const { userProfile } = useProfileContext();
  return {
    key: ["ai-assistant", userProfile?.organisation_id, userProfile?.user_id, "knowledge"],
    enabled: !!userProfile?.user_id && userProfile?.user_role === "Owner",
  };
}

function useKnowledgeQuery(suffix, queryFn, { enabled = true, poll = false, ...options } = {}) {
  const scope = useKnowledgeScope();
  const identity = JSON.stringify([...scope.key, ...suffix]);
  const windowRef = useRef({ identity, started: null, paused: false });
  if (windowRef.current.identity !== identity) windowRef.current = { identity, started: null, paused: false };
  const query = useQuery({
    queryKey: [...scope.key, ...suffix], queryFn,
    enabled: scope.enabled && enabled, retry: false, staleTime: 30000,
    ...(poll ? { refetchInterval: (current) => knowledgePollInterval(current, windowRef.current), refetchIntervalInBackground: false } : {}),
    ...options,
  });
  return { ...query, pollingPaused: windowRef.current.paused,
    refetch: () => { windowRef.current.started = null; windowRef.current.paused = false; return query.refetch(); } };
}

export const useKnowledgeAdapters = () => useKnowledgeQuery(["adapters"], ({ signal }) => api.getKnowledgeAdapters(signal), { staleTime: 300000 });
export const useKnowledgeSources = (params = { page: 1, limit: 20 }) => useKnowledgeQuery(["sources", params], ({ signal }) => api.getKnowledgeSources(params, signal), { poll: true });
export function useKnowledgeSource(sourceId, options = {}) {
  const query = useKnowledgeQuery(["source", sourceId], ({ signal }) => api.getKnowledgeSource(sourceId, signal), { enabled: !!sourceId, poll: true, ...options });
  const client = useQueryClient();
  const { key } = useKnowledgeScope();
  const scopeIdentity = JSON.stringify(key);
  const updated = query.data?.updated_at;
  const pipeline = query.data?.latest_sync_run?.pipeline_status;
  useEffect(() => {
    if (updated) client.invalidateQueries({ queryKey: [...JSON.parse(scopeIdentity), "runs", sourceId] });
  }, [client, scopeIdentity, sourceId, updated, pipeline]);
  return query;
}

export function useKnowledgeSourceOptions() {
  const { key, enabled } = useKnowledgeScope();
  return useInfiniteQuery({ queryKey: [...key, "source-options"], enabled, initialPageParam: 1,
    queryFn: ({ pageParam, signal }) => api.getKnowledgeSources({ page: pageParam, limit: 100 }, signal),
    getNextPageParam: page => page.page * page.limit < page.total && page.page < 10000 ? page.page + 1 : undefined,
    retry: false, staleTime: 60000,
  });
}
export const useKnowledgeSyncRuns = (sourceId, params = { page: 1, limit: 10 }) => useKnowledgeQuery(["runs", sourceId, params], ({ signal }) => api.getKnowledgeSyncRuns(sourceId, params, signal), { enabled: !!sourceId });
export const useKnowledgeEntities = (params) => useKnowledgeQuery(["entities", params], ({ signal }) => api.getKnowledgeEntities(params, signal));
export const useKnowledgeEntity = (entityId) => useKnowledgeQuery(["entity", entityId], ({ signal }) => api.getKnowledgeEntity(entityId, signal), { enabled: !!entityId });

function useKnowledgeMutation(mutationFn, onSuccess) {
  const client = useQueryClient();
  const scope = useKnowledgeScope();
  return useMutation({ mutationFn, retry: false,
    // Retain the initiating account's cache scope if profile changes while the
    // request is in flight. Never merge its response into the next user's UI.
    onMutate: () => ({ key: scope.key }),
    onSuccess: (data, variables, context) => onSuccess?.(client, context.key, data, variables),
    onSettled: (data, error, variables, context) => { client.invalidateQueries({ queryKey: context.key }); },
  });
}

export const useCreateKnowledgeSource = () => useKnowledgeMutation(api.createKnowledgeSource);
export const useUpdateKnowledgeSource = () => useKnowledgeMutation(api.updateKnowledgeSource,
  (client, key, data) => client.setQueryData([...key, "source", data.id], data));
export const useSyncKnowledgeSource = () => useKnowledgeMutation(api.syncKnowledgeSource,
  (client, key, data) => {
    const queued = source => source?.id === data.source_id ? { ...source, reserved: true, activity_status: "queued" } : source;
    client.setQueryData([...key, "source", data.source_id], queued);
    client.setQueriesData({ queryKey: [...key, "sources"] }, previous => previous ? { ...previous, items: previous.items.map(queued) } : previous);
  });
export const useUpdateKnowledgeEntity = () => useKnowledgeMutation(api.updateKnowledgeEntity,
  (client, key, data) => {
    client.setQueryData([...key, "entity", data.id], previous => previous ? { ...previous, ...data } : previous);
  });
